/** Master-Paket 1 — Leads list + detail. */
import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

function StatusBadge({ status }) {
  const cls = {
    delivered: "bg-emerald-600",
    queued: "bg-blue-500",
    pending: "bg-amber-500",
    skipped_no_crm: "bg-slate-500",
    skipped_synthetic: "bg-purple-500",
    skipped_duplicate: "bg-purple-500",
    failed: "bg-red-600",
  }[status] || "bg-slate-400";
  return <Badge className={cls} data-testid={`delivery-status-${status}`}>{status}</Badge>;
}

function QualityBar({ score, band }) {
  const color = band === "high" ? "bg-emerald-500"
    : band === "medium" ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-2" data-testid="quality-bar">
      <div className="w-24 h-2 bg-muted rounded overflow-hidden">
        <div className={`${color} h-full`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs text-muted-foreground w-8">{score}</span>
    </div>
  );
}

export function LeadsList() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    status: "", quality_band: "", campaign_id: "", delivery_status: "",
    include_duplicates: false, include_synthetic: false,
  });
  const [hasCrm, setHasCrm] = useState(true);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v !== false) params.set(k, String(v));
    });
    try {
      const r = await api.get(`/leads?${params.toString()}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      // Detect CRM presence by scanning delivery_status
      const anyNoCrm = (r.data.items || []).some(i => i.delivery_status === "skipped_no_crm");
      setHasCrm(!anyNoCrm || (r.data.items || []).length === 0);
    } catch (e) { toast.error("Load failed"); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  async function exportCsv() {
    try {
      const params = new URLSearchParams();
      if (filters.include_duplicates) params.set("include_duplicates", "true");
      if (filters.include_synthetic) params.set("include_synthetic", "true");
      const r = await api.get(`/leads/export.csv?${params.toString()}`,
                                        { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "leads.csv"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export bereit");
    } catch (e) { toast.error("Export fehlgeschlagen"); }
  }

  return (
    <div className="space-y-4" data-testid="leads-page">
      <header>
        <h1 className="text-2xl font-semibold">Leads</h1>
        <p className="text-sm text-muted-foreground">
          Nativ verwaltet · CRM optional
        </p>
      </header>

      {!hasCrm && (
        <div className="p-3 bg-blue-50 border-blue-200 border rounded text-sm"
                data-testid="no-crm-banner">
          Kein CRM verbunden — Leads werden nativ verwaltet. Übertragung an CRM
          erfolgt sobald eine Verbindung konfiguriert ist.
        </div>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Filter</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Select value={filters.status || "any"}
                    onValueChange={(v) => setFilters({...filters, status: v === "any" ? "" : v})}>
            <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Alle</SelectItem>
              <SelectItem value="new">Neu</SelectItem>
              <SelectItem value="contacted">Kontaktiert</SelectItem>
              <SelectItem value="qualified">Qualifiziert</SelectItem>
              <SelectItem value="disqualified">Disqualifiziert</SelectItem>
              <SelectItem value="won">Gewonnen</SelectItem>
              <SelectItem value="lost">Verloren</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.quality_band || "any"}
                    onValueChange={(v) => setFilters({...filters, quality_band: v === "any" ? "" : v})}>
            <SelectTrigger data-testid="filter-band"><SelectValue placeholder="Qualität" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Alle</SelectItem>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="campaign_id" value={filters.campaign_id}
                    data-testid="filter-campaign"
                    onChange={(e) => setFilters({...filters, campaign_id: e.target.value})} />
          <Button variant="outline" onClick={exportCsv} data-testid="btn-export-csv">
            CSV Export
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {total} Lead{total !== 1 ? "s" : ""}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead className="text-left border-b">
              <tr>
                <th className="p-2">Zeit</th>
                <th className="p-2">Kontakt</th>
                <th className="p-2">Kampagne</th>
                <th className="p-2">Qualität</th>
                <th className="p-2">Delivery</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((l) => (
                <tr key={l.id} className="border-b hover:bg-muted/50"
                      data-testid={`lead-row-${l.id}`}>
                  <td className="p-2 whitespace-nowrap text-xs">
                    {(l.created_at || "").slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="p-2">
                    <div>{(l.contact || {}).email || "—"}</div>
                    <div className="text-xs text-muted-foreground">
                      {(l.contact || {}).phone || ""}
                    </div>
                  </td>
                  <td className="p-2 text-xs">{l.campaign_id || "—"}</td>
                  <td className="p-2">
                    <QualityBar score={l.quality_score || 0} band={l.quality_band} />
                  </td>
                  <td className="p-2">
                    <StatusBadge status={l.delivery_status} />
                    {l.environment === "preview" && (
                      <span className="ml-1 text-xs px-1.5 py-0.5 border rounded bg-amber-50 border-amber-300 text-amber-800"
                              data-testid={`env-chip-preview-${l.id}`}>
                        preview
                      </span>
                    )}
                    {l.data_source === "synthetic" && (
                      <span className="ml-1 text-xs px-1.5 py-0.5 border rounded bg-slate-100 border-slate-300 text-slate-700"
                              data-testid={`ds-chip-synthetic-${l.id}`}>
                        synthetic
                      </span>
                    )}
                  </td>
                  <td className="p-2">
                    <Link to={`/app/leads/${l.id}`}
                            className="text-blue-600 hover:underline text-xs"
                            data-testid={`lead-link-${l.id}`}>Details</Link>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">
                  Keine Leads.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

export function LeadDetail() {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await api.get(`/leads/${id}`);
      setLead(r.data);
    } catch { toast.error("Nicht gefunden"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  async function retry() {
    try {
      await api.post(`/leads/${id}/retry-delivery`);
      toast.success("Wieder zugestellt");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "Fehler");
    }
  }

  if (!lead) return <div className="p-4">Lade …</div>;
  const c = lead.contact || {};
  const breakdown = lead.quality_breakdown || [];

  return (
    <div className="space-y-4" data-testid="lead-detail-page">
      <header>
        <h1 className="text-2xl font-semibold">Lead {lead.id.slice(0, 8)}…</h1>
        <p className="text-sm text-muted-foreground">
          {(lead.created_at || "").slice(0, 19).replace("T", " ")}
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">Kontakt</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            <div><strong>Name:</strong> {c.name || "—"}</div>
            <div><strong>Email:</strong> {c.email || "—"}</div>
            <div><strong>Phone:</strong> {c.phone || "—"}</div>
            {lead.message && <div className="mt-2 text-xs text-muted-foreground">
              „{lead.message}"</div>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Delivery</CardTitle></CardHeader>
          <CardContent>
            <StatusBadge status={lead.delivery_status} />
            {lead.delivery_status === "failed" && (
              <Button className="ml-2" size="sm" onClick={retry}
                        data-testid="btn-retry">
                Wieder zustellen
              </Button>
            )}
            <div className="mt-3 text-xs">
              {(lead.outbox_history || []).map((o) => (
                <div key={o.id} className="border-b py-1">
                  {o.status} · attempts: {o.attempts}
                  {o.last_error && <span className="text-red-500"> · {o.last_error}</span>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="quality-breakdown">
        <CardHeader>
          <CardTitle className="text-base">
            Qualitäts-Score {lead.quality_score}/100 ({lead.quality_band})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-3 p-3 border border-amber-300 bg-amber-50 rounded"
                 data-testid="quality-semantics-alert">
            <div className="font-semibold text-sm">
              Regelbasierte Einschätzung, keine Konversionsprognose
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {lead.quality_score_semantics ||
                "Signal aus Formular-Vollständigkeit + Consent. KEINE Aussage über Abschluss-Wahrscheinlichkeit."}
              {" "}Version <span className="font-mono">{lead.quality_score_version}</span>.
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="text-left border-b">
              <tr><th className="p-1">Regel</th><th className="p-1">Punkte</th>
                    <th className="p-1">Begründung</th><th className="p-1">Beleg</th></tr>
            </thead>
            <tbody>
              {breakdown.map((b, i) => (
                <tr key={i} className="border-b" data-testid={`rule-${b.rule}`}>
                  <td className="p-1 font-mono text-xs">{b.rule}</td>
                  <td className="p-1">{b.points}</td>
                  <td className="p-1 text-xs">{b.reason}</td>
                  <td className="p-1 text-xs text-muted-foreground">
                    {typeof b.evidence === "object"
                      ? JSON.stringify(b.evidence)
                      : String(b.evidence)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

export default LeadsList;
